import { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { isNativeApp } from "./lib/platform";
import SplashScreen from "./components/SplashScreen";
import { useCircadianMode } from "./lib/useCircadianMode";

// Pages — route-level lazy loading keeps the initial entry bundle light.
// Heavy deps now split into per-route chunks instead of the entry bundle:
//   three.js / @react-three  → Chat (Aura) + Eden experiences
//   maplibre-gl / pmtiles    → Local routes
//   recharts / framer-motion → the specific routes that use them
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const VerifyPendingPage = lazy(() => import("./pages/VerifyPendingPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const DiscoveryPage = lazy(() => import("./pages/DiscoveryPage"));
const MatchesPage = lazy(() => import("./pages/MatchesPage"));
const ChatsPage = lazy(() => import("./pages/ChatsPage"));
const ChatThreadPage = lazy(() => import("./pages/ChatThreadPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const BlockedUsersPage = lazy(() => import("./pages/BlockedUsersPage"));
const OfferingsPage = lazy(() => import("./pages/OfferingsPage"));
const StorePage = lazy(() => import("./pages/StorePage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const LocalPage = lazy(() => import("./pages/LocalPage"));
const LocalMinePage = lazy(() => import("./pages/LocalMinePage"));
const LocalWisdomPage = lazy(() => import("./pages/LocalWisdomPage"));
const LocalGroceryPage = lazy(() => import("./pages/LocalGroceryPage"));
const EdenLabPage = lazy(() => import("./pages/EdenLabPage"));
const FlockwardPage = lazy(() => import("./pages/FlockwardPage"));
const GreenPillPage = lazy(() => import("./pages/GreenPillPage"));
const LocalWisdomPrintPage = lazy(() => import("./pages/LocalWisdomPrintPage"));
const StewardPage = lazy(() => import("./pages/StewardPage"));
const ConnectPage = lazy(() => import("./pages/ConnectPage"));
const HomePage = lazy(() => import("./pages/HomePage"));
const StewardLocalPage = lazy(() => import("./pages/StewardLocalPage"));
const StewardCryptoPage = lazy(() => import("./pages/StewardCryptoPage"));

// Components
import BottomNav from "./components/BottomNav";
import UpgradeModal from "./components/UpgradeModal";
import InstallPrompt from "./components/InstallPrompt";
import AgeGate from "./components/AgeGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Lightweight, ETHOS-branded fallback shown while a lazy route chunk loads.
// Announced to assistive tech (role=status) and reduced-motion aware
// (spin/pulse only under motion-safe; static ring under prefers-reduced-motion).
const RouteFallback = () => (
  <div
    role="status"
    aria-live="polite"
    aria-label="Loading"
    style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
  >
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
      <div
        className="motion-safe:animate-spin"
        style={{
          width: 40, height: 40, borderRadius: "9999px",
          border: "2px solid rgba(107,78,138,0.22)", borderTopColor: "#6B4E8A",
        }}
      />
      <span style={{ fontSize: 12, letterSpacing: "0.14em", color: "#9A928A", textTransform: "uppercase" }}>
        ETHOS
      </span>
    </div>
  </div>
);

// Screenshot / demo mode — activated by ?screenshot=1 in URL
// Suppresses install prompts and non-essential banners for clean screenshots.
const _ssParam = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("screenshot") === "1";
if (_ssParam) {
  try { localStorage.setItem("ethos_screenshot_mode", String(Date.now())); } catch { /* ignore storage errors */ }
}
const _ssSaved = (() => {
  try {
    const ts = parseInt(localStorage.getItem("ethos_screenshot_mode") || "0", 10);
    return ts > 0 && Date.now() - ts < 14 * 24 * 60 * 60 * 1000; // 14-day window
  } catch { return false; }
})();
export const SCREENSHOT_MODE = _ssParam || _ssSaved;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// API instance with auth
export const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // A failed login must show its error, and an old request must not clear a newer session.
    const sentToken = error.config?.headers?.Authorization;
    const currentToken = localStorage.getItem("token");
    const isLoginRequest = /\/auth\/(login|register|signup)(?:$|[?#])/.test(error.config?.url || "");
    if (error.response?.status === 401 && !isLoginRequest && currentToken && sentToken === 'Bearer ' + currentToken) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/auth";
    }
    return Promise.reject(error);
  }
);

// Protected Route - checks email verification + 18+ age gate
const ProtectedRoute = ({ children }) => {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  // If user hasn't verified email, show verification pending page
  if (!user.email_verified) {
    return <VerifyPendingPage email={user.email} onLogout={logout} />;
  }

  // 18+ age gate — block social/matching surfaces until age is confirmed.
  // Allowlisted areas (settings, terms, privacy) remain reachable so users can
  // read policies, log out, or delete their account.
  const ageOk =
    user.age_confirmed_18_plus === true ||
    ["self_declared", "verified_18_plus", "exempt"].includes(user.age_verification_status);
  const ageAllowlist = ["/settings", "/terms", "/privacy"];
  if (!ageOk && !ageAllowlist.some((p) => location.pathname.startsWith(p))) {
    return <AgeGate />;
  }
  
  return children;
};

// Layout with bottom nav
const AppLayout = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  // When logged out, don't show the primary nav on public Local Wisdom — its
  // links lead to protected areas. Logged-in users keep the nav as before.
  const loggedOutWisdom = !user && location.pathname.startsWith("/local/wisdom");
  const hideNav = ["/games/flockward", "/games/green-pill"].includes(location.pathname) || ["/auth", "/profile/edit", "/terms", "/privacy", "/verify-email", "/reset-password", "/admin", "/connect/"].some(p => location.pathname.startsWith(p)) || location.pathname === "/" || location.pathname.includes("/chat/") || loggedOutWisdom;
  const layoutClassName = hideNav ? "mobile-wrapper" : "mobile-wrapper mobile-wrapper--nav-pad";
  
  return (
    <div className={layoutClassName}>
      {children}
      {!hideNav && <BottomNav />}
    </div>
  );
};

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [splashDone, setSplashDone] = useState(false);
  // Only show app-load splash for returning users (stored token present)
  // New users get the post-login splash from AuthPage instead
  const [hasStoredToken] = useState(() => !!localStorage.getItem("token"));
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Circadian sanctuary mode — shifts CSS variables after 7pm / before 6am
  useCircadianMode();

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await apiClient.get("/auth/me");
          if (localStorage.getItem("token") !== token) return;
          setUser(response.data);
          
          // Check for session_id from Stripe redirect
          const sessionId = searchParams.get("session_id");
          if (sessionId) {
            pollPaymentStatus(sessionId);
          }
        } catch (e) {
          if (e.response?.status === 401 && localStorage.getItem("token") === token) {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info("Payment status check timed out. Please check your email for confirmation.");
      return;
    }

    try {
      const response = await apiClient.get(`/subscription/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        toast.success("Welcome to Ethos Plus!");
        const meResponse = await apiClient.get("/auth/me");
        setUser(meResponse.data);
        navigate("/settings", { replace: true });
        return;
      } else if (response.data.status === "expired") {
        toast.error("Payment session expired. Please try again.");
        return;
      }
      
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  };

  useEffect(() => {
    const sessionChanged = (event) => {
      if (event.storageArea === localStorage && (event.key === "token" || event.key === null)) {
        // A full reload unmounts private screens and sockets on sign-out/account change.
        window.location.replace("/auth");
      }
    };
    window.addEventListener("storage", sessionChanged);
    return () => window.removeEventListener("storage", sessionChanged);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    
    // Redirect to profile edit if profile incomplete OR missing username
    if (!userData.profile_complete || !userData.username) {
      navigate("/profile/edit");
    } else {
      navigate("/discover");
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    window.location.replace("/auth");
  };

  const refreshUser = async () => {
    try {
      const response = await apiClient.get("/auth/me");
      setUser(response.data);
    } catch (e) {
      console.error("Failed to refresh user", e);
    }
  };

  const triggerUpgrade = (reason) => {
    if (isNativeApp()) {
      toast.info("Paid upgrades are available on the web.");
      return;
    }
    setUpgradeReason(reason);
    setShowUpgrade(true);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, triggerUpgrade }}>
      {/* Splash — only shown for returning users on app load */}
      {loading && !splashDone && hasStoredToken && (
        <SplashScreen onDone={() => setSplashDone(true)} />
      )}
      <div className="app-container">
        <AppLayout>
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/auth" element={user ? <Navigate to="/discover" replace /> : <AuthPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            {/* Public invite landing — no auth required */}
            <Route path="/connect/:username" element={<ConnectPage />} />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/profile/edit" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/discover" element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>} />
            <Route path="/discovery" element={<ProtectedRoute><DiscoveryPage /></ProtectedRoute>} />
            <Route path="/matches" element={<ProtectedRoute><MatchesPage /></ProtectedRoute>} />
            <Route path="/chats" element={<ProtectedRoute><ChatsPage /></ProtectedRoute>} />
            <Route path="/chat/:matchId" element={<ProtectedRoute><ChatThreadPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/blocked" element={<ProtectedRoute><BlockedUsersPage /></ProtectedRoute>} />
            <Route path="/offerings" element={<ProtectedRoute><OfferingsPage /></ProtectedRoute>} />
            <Route path="/store" element={<ProtectedRoute><StorePage /></ProtectedRoute>} />
            <Route path="/local" element={<ProtectedRoute><LocalPage /></ProtectedRoute>} />
            <Route path="/local/mine" element={<ProtectedRoute><LocalMinePage /></ProtectedRoute>} />
            <Route path="/local/grocery" element={<ProtectedRoute><LocalGroceryPage /></ProtectedRoute>} />
            {/* Public Local Wisdom — readable without login (static, read-only) */}
            <Route path="/local/wisdom" element={<LocalWisdomPage />} />
            <Route path="/eden-lab" element={<EdenLabPage />} />
            <Route path="/games/flockward" element={<ProtectedRoute><FlockwardPage /></ProtectedRoute>} />
            <Route path="/games/green-pill" element={<ProtectedRoute><GreenPillPage /></ProtectedRoute>} />
            <Route path="/local/wisdom/print/:guideId" element={<LocalWisdomPrintPage />} />
            <Route path="/steward" element={<ProtectedRoute><StewardPage /></ProtectedRoute>} />
            <Route path="/steward/local" element={<ProtectedRoute><StewardLocalPage /></ProtectedRoute>} />
            <Route path="/steward/crypto" element={<ProtectedRoute><StewardCryptoPage /></ProtectedRoute>} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/" element={user ? <Navigate to="/discover" replace /> : <HomePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </AppLayout>
        
        {!isNativeApp() && (
          <UpgradeModal 
            isOpen={showUpgrade} 
            onClose={() => setShowUpgrade(false)} 
            reason={upgradeReason}
          />
        )}
        
        <InstallPrompt />
      </div>
      <Toaster position="bottom-center" offset={{ bottom: "92px" }} mobileOffset={{ bottom: "92px" }} richColors />
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
